import { useState, useEffect, lazy, Suspense } from "react";
import { CheckCircle2, TrendingUp, ExternalLink, ArrowRight } from "lucide-react";
import { getVisitorIntent } from "@/utils/visitorIntent";
import { getUpdatedDate } from "@/utils/dateUtils";
import { topPrivateLoans } from "@/data/featuredProducts";
import { samblaData } from "@/data/providers/sambla";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrustpilotRating } from "./TrustpilotRating";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { Link } from "react-router-dom";
import heroBackground from "@/assets/newhero_origami_compressed.webp";
import warningIcon from "@/assets/varning.webp";
import { trackAffiliateClick } from "@/utils/gtmTracking";

const Alert = lazy(() => import("@/components/ui/alert").then(mod => ({ default: mod.Alert })));
const AlertDescription = lazy(() => import("@/components/ui/alert").then(mod => ({ default: mod.AlertDescription })));

const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  const [heroText, setHeroText] = useState({
    greeting: "Jämför lån för alla ändamål",
    highlight: "tryggt och enkelt",
    subtext: "Vi handplockar de mest pålitliga och omtyckta långivarna och låneförmedlarna - helt oberoende och kostnadsfritt."
  });
  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      // Only personalize on desktop, and use requestIdleCallback to avoid blocking
      if (!mobile && 'requestIdleCallback' in window) {
        requestIdleCallback(() => {
          const intent = getVisitorIntent();
          const now = new Date().getHours();

          let greeting = "Jämför lån för alla ändamål";
          if (now >= 5 && now < 12) greeting = "God morgon! Redo att hitta";
          else if (now >= 12 && now < 18) greeting = "God eftermiddag! Leta efter";
          else if (now >= 18 && now < 23) greeting = "God kväll! Dags att hitta";

          if (intent.top_category === 'credit_card') {
            setHeroText({
              greeting: greeting,
              highlight: "bästa kreditkortet",
              subtext: "Cashback, reseförsäkringar och bonuspoäng - jämför alla fördelar."
            });
          } else if (intent.top_category === 'savings') {
            setHeroText({
              greeting: greeting,
              highlight: "högsta sparräntan",
              subtext: "Upp till 3.85% ränta med insättningsgaranti - säkert och tryggt."
            });
          } else if (intent.is_returning && intent.last_name) {
            setHeroText({
              greeting: `Välkommen tillbaka, ${intent.last_name}!`,
              highlight: "Fortsätt där du slutade",
              subtext: "Vi har sparat dina jämförelser så du kan fortsätta direkt."
            });
          }
        }, { timeout: 2000 });
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return <section id="hero-section" className="relative pt-20 lg:pt-28 pb-8 md:pb-12 lg:pb-32 overflow-hidden">
      {/* Mobile/Tablet Blue Gradient Background with Overlay */}
      <div className="absolute inset-0 z-0 lg:hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(210,70%,25%)] via-[hsl(210,65%,35%)] to-[hsl(210,60%,45%)]" />
        <div className="absolute inset-0 bg-black/30" />
      </div>
      
      {/* Desktop Image Background */}
      <div className="absolute inset-0 z-0 hidden lg:block">
        <OptimizedImage 
          src={heroBackground} 
          alt="" 
          priority={true}
          loading="eager"
          fetchPriority="high"
          objectFit="cover"
          className="w-full h-full object-[left_30%]"
          width={1600}
          height={1200}
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>
      <div className="container mx-auto px-3 md:px-4 relative z-20">
        <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-start">
          {/* Left: Text Content */}
          <div className="space-y-4 md:space-y-6 hero-content">
            <Badge className="mb-4 bg-primary/20 text-white border-white/30" variant="outline">
              Bolån • Privatlån • Företagslån
            </Badge>
            
            <h1 key={heroText.greeting} className="text-3xl md:text-5xl lg:text-6xl font-bold text-balance leading-tight text-white hero-title mb-4 md:mb-6">
              {heroText.greeting}<span className="text-white">.</span>
              <br />
              {heroText.highlight === "tryggt och enkelt" ? (
                <>
                  <span className="text-[hsl(142,70%,65%)]">tryggt</span>
                  {" "}
                  <span className="text-white">och</span>
                  {" "}
                  <span className="text-[hsl(142,70%,65%)]">enkelt</span><span className="text-white">.</span>
                </>
              ) : (
                <span className="text-[hsl(142,70%,65%)]">{heroText.highlight}</span>
              )}
            </h1>
            
            <p className="text-base md:text-xl text-white/90 text-balance mb-6 md:mb-8">
              {heroText.subtext}
            </p>

            {/* Trust Badges - Desktop */}
            <div className="hidden md:flex flex-col gap-4 pt-4">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0" />
                <p className="font-semibold text-white">Tryggt, enkelt och transparent</p>
              </div>
              
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0" />
                <p className="font-semibold text-white">Alltid uppdaterade räntor och villkor</p>
              </div>
              
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0" />
                <p className="font-semibold text-white">För dig som vill ligga steget före</p>
              </div>
            </div>

            {/* Trust Badges - Mobile */}
            <div className="flex md:hidden flex-col gap-2 pt-4">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                <p className="text-sm font-semibold text-white">Tryggt, enkelt och transparent</p>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                <p className="text-sm font-semibold text-white">Alltid uppdaterade räntor och villkor</p>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                <p className="text-sm font-semibold text-white">För dig som vill ligga steget före</p>
              </div>
            </div>
          </div>

          {/* Right: Featured Product */}
          <div className="max-w-lg mx-auto lg:mx-0 lg:ml-12 lg:max-w-xl md:pt-0">
            <div className="space-y-3 md:space-y-4">
              <div className="text-center mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  Låneförmedlaren med flest nöjda kunder
                </h2>
                <p className="text-white/90 text-sm font-medium">Uppdaterad {getUpdatedDate()}</p>
              </div>

              <Card className="p-4 md:p-6 space-y-3 md:space-y-4 backdrop-blur-sm bg-card/95 border-2 border-primary/20 shadow-xl">
                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                  Jämför 40 långivare
                </Badge>
                
                  <div className="space-y-3">
                    <div className="flex justify-center">
                      <img 
                        src={samblaData.logo} 
                        alt="Sambla" 
                        className="h-12 md:h-16 lg:h-20 w-auto object-contain"
                        width="600"
                        height="150"
                        loading="eager"
                        decoding="async"
                      />
                    </div>
                  <div className="flex items-center justify-center gap-2">
                    <TrustpilotRating rating={4.8} size="md" showScore={true} showLogo={true} />
                    <span className="text-xs text-muted-foreground">
                      (29186 omdömen)
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4 py-3 md:py-4 border-y border-border">
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Effektiv ränta</p>
                    <p className="font-bold text-primary text-2xl md:text-3xl">från 5,08%</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Lånebelopp</p>
                    <p className="text-sm font-semibold text-foreground mt-1">5 000 - 800 000 kr</p>
                  </div>
                </div>

                <ul className="space-y-2">
                  {[
                    "Jämför privatlån från 40 olika långivare med bara 1 UC-koll",
                    "Svar samma dag från flera långivare",
                    "Kostnadsfri jämförelse och professionell hjälp"
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <TrendingUp className="h-3 w-3 text-primary" />
                      </div>
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="space-y-3">
                  <Button 
                    asChild 
                    size="lg" 
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => trackAffiliateClick({
                      provider: 'Sambla',
                      pageType: 'homepage',
                      section: 'hero'
                    })}
                  >
                    <a href="https://www.sambla.se/lana-pengar/" target="_blank" rel="noopener noreferrer">
                      Ansök hos Sambla
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  
                  <Button asChild size="lg" variant="outline" className="w-full border border-muted-foreground/30 text-foreground bg-transparent hover:bg-transparent hover:text-foreground hover:border-muted-foreground/50 hover:shadow-lg transition-all duration-300 group md:hover:-translate-y-0.5">
                    <Link to="/sv-SE/privatlan/sambla" className="no-underline">
                      Läs mer om Sambla
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>

                <Suspense fallback={<div className="mt-6 h-24" />}>
                  <Alert className="border-red-600/50 bg-red-50/10 dark:bg-red-950/20 flex items-start gap-3">
                    <img 
                      src={warningIcon} 
                      alt="Varning" 
                      className="h-12 w-12 flex-shrink-0"
                      width="48"
                      height="48"
                      loading="lazy"
                      decoding="async"
                    />
                    <AlertDescription className="text-xs text-foreground flex-1">
                      <strong>Att låna kostar pengar!</strong>
                      <br /><br />
                      Om du inte kan betala tillbaka skulden i tid riskerar du en betalningsanmärkning. 
                      Det kan leda till svårigheter att få hyra bostad, teckna abonnemang och få nya lån. 
                      För stöd, vänd dig till budget- och skuldrådgivningen i din kommun. 
                      Kontaktuppgifter finns på{" "}
                      <a href="https://www.konsumentverket.se" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
                        konsumentverket.se
                      </a>
                    </AlertDescription>
                  </Alert>
                </Suspense>
              </Card>
            </div>
          </div>
        </div>

      </div>
    </section>;
};
export default HeroSection;
